/**
 * SocialNotifier — global on-screen pop-ups for social events.
 * Polls the user's notification feed and shows a toast (auto-dismiss ~8s, with
 * a close X and an "Open" action that navigates) whenever a NEW friend request,
 * friend-accept, message, or push-to-talk arrives. Runs on EVERY signed-in
 * account (kids included). Mounted once in the header.
 */
import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { playNotificationSound } from "../utils/notificationSound";
import useUserNotifySocket from "../hooks/useUserNotifySocket";

const API = process.env.REACT_APP_BACKEND_URL;
const POPUP_TYPES = new Set(["friend_request", "friend_accept", "friend_message", "friend_voice"]);

export default function SocialNotifier({ user }) {
  const navigate = useNavigate();
  const seen = useRef(null); // null until first load, then a Set of seen ids
  const socketUid = user && !user.isDemo ? (user.id || user.email) : null;
  // App-wide instant channel: fires iw:social-event the moment a notification
  // is pushed, so the toast appears immediately (not on the poll timer).
  useUserNotifySocket(socketUid);

  const poll = useCallback(async () => {
    if (!user || user.isDemo) return;
    const uid = user.id || user.email;
    if (!uid) return;
    try {
      const r = await axios.get(`${API}/api/notifications/user/${uid}?limit=15`);
      const list = r.data.notifications || [];
      // First run: remember what's already there, don't pop up old items.
      if (seen.current === null) {
        seen.current = new Set(list.map((n) => n.id));
        return;
      }
      // Show oldest-first so multiple new items stack in order.
      for (const n of [...list].reverse()) {
        if (!n.id || seen.current.has(n.id)) continue;
        seen.current.add(n.id);
        if (n.read || !POPUP_TYPES.has(n.type)) continue;
        // Play the alert sound, but NEVER let an audio failure (e.g. iOS Safari
        // Web-Audio restrictions) stop the on-screen pop-up from showing — the
        // toast must appear on mobile even if sound is blocked.
        try { playNotificationSound(n.from_user_id); } catch { /* sound optional */ }
        toast(n.title, {
          description: n.message,
          duration: 8000,
          closeButton: true,
          action: n.action_url
            ? { label: "Open", onClick: () => {
                const m = /[?&]chat=([^&]+)/.exec(n.action_url);
                if (m) {
                  const fid = n.from_user_id || decodeURIComponent(m[1]);
                  window.dispatchEvent(new CustomEvent("iw:open-friend-chat", { detail: { id: fid, name: n.from_name } }));
                }
                navigate(n.action_url);
              } }
            : undefined,
        });
      }
      if (seen.current.size > 400) seen.current = new Set(list.map((n) => n.id));
    } catch { /* silent */ }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.isDemo) return;
    seen.current = null;
    poll();
    const id = setInterval(poll, 5000);
    // Instant: re-poll the moment the live socket signals a new notification.
    const onEvent = () => poll();
    window.addEventListener("iw:social-event", onEvent);
    return () => { clearInterval(id); window.removeEventListener("iw:social-event", onEvent); };
  }, [user, poll]);

  // App-wide presence heartbeat so friends see you online anywhere in the app
  // (not only while the Friends page is open). Per-profile online status.
  useEffect(() => {
    if (!socketUid) return;
    const beat = () => axios.post(`${API}/api/friends/presence`, { user_id: socketUid }).catch(() => {});
    beat();
    const id = setInterval(beat, 30000);
    return () => clearInterval(id);
  }, [socketUid]);

  return null;
}
